/**
 * WikiGraphPanel — a right-docked panel (same chrome as the artifact panel,
 * light theme) that illustrates the notes the agent jumps to, in real time.
 *
 * Default mode = "trail": only the visited notes are drawn (labeled), connected
 * by the jump path, growing as the agent reads and pulsing the current note.
 * Mode "full" draws the whole wiki graph, Obsidian-style: nodes sized by link
 * degree, labels fading in with zoom, hover highlighting a node's neighborhood,
 * and a live force simulation that unfolds incrementally — it never blocks the
 * UI, whatever the wiki size (see wikiForceGraph.ts).
 *
 * The two modes are toggled by a floating segmented control on the canvas.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@hooks/useLanguage';
import api from '@services/api';
import type { WikiNote, WikiEdge } from '@modules/wiki/hooks/useWikiGraph';
import { wikiPageKey } from '@modules/wiki/utils/pageRef';
import { WikiForceGraph, type GraphEdgeInput } from './wikiForceGraph';
import './WikiGraphPanel.css';

/** "W" mark — the industry wiki glyph. Used by the unified side panel's
 *  Wiki tab (ConversationPanel). */
export function WikiWIcon({ size = 15 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 5l4.5 14L12 8l4.5 11L21 5" />
    </svg>
  );
}

interface Props {
  notes: WikiNote[];            // visited notes (unique, ordered)
  sequence: string[];           // ordered note keys → jump edges
  currentId: string | null;
  mode: 'trail' | 'full';
  fullNodes: WikiNote[];
  fullEdges: WikiEdge[];
  fullLoading: boolean;
  conversationId?: string | null;  // resolves the source-preview fetch
  onResize: (w: number) => void;
  onSetMode: (m: 'trail' | 'full') => void;
}

/**
 * Render Obsidian-style [[wikilinks]] as plain, readable text so the preview
 * reads naturally (the note files aren't a browsable web here):
 *   [[Page]]            → "Page"
 *   [[target|Alias]]    → "Alias"
 *   [[Page#Section]]    → "Page"
 *   ![[embed]]          → "" (embeds have no target to show inline)
 */
function stripWikiLinks(md: string): string {
  if (!md) return '';
  return md
    .replace(/!\[\[[^\]]*\]\]/g, '')                 // drop transclusion embeds
    .replace(/\[\[([^\]]+)\]\]/g, (_m, inner) => {
      const [target, alias] = String(inner).split('|');
      return (alias ?? target.split('#')[0]).trim();
    });
}

// Folder palette tuned for a LIGHT background.
const FOLDER_COLORS: Record<string, string> = {
  essays: '#2563eb', parsha: '#059669', themes: '#db2777', concepts: '#d97706',
  figures: '#7c3aed', philosophy: '#e11d48', teaching: '#0891b2', meta: '#64748b', '': '#64748b',
};
const folderColor = (f: string) => FOLDER_COLORS[f] ?? '#64748b';
const ACCENT = '#2563eb';
// Obsidian-style full map: uniform GRAY nodes; purple is the focus color for
// the current/hovered note and its links (matches Obsidian's graph view).
const MAP_NODE_COLOR = '#7d8590';
const MAP_HIGHLIGHT = '#7c6af0';

// A full-map node's id is its path; the trail keys every page the same way.
const baseKey = wikiPageKey;

const TRAIL_STYLE = { linkDistance: 84, baseRadius: 6, degreeRadius: 1.1, maxRadius: 10 };
// Wide radius range so hub/leaf size differences read clearly at a glance.
const FULL_STYLE = { linkDistance: 46, baseRadius: 2, degreeRadius: 1.15, maxRadius: 14 };

const NetworkIcon = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="6" cy="6" r="2.6" />
    <circle cx="18" cy="8" r="2.2" />
    <circle cx="9" cy="18" r="2.4" />
    <path d="M8.2 7.3l7.6 0M7.1 8.4l1.4 7.2M10.8 16.6l5.8-6.6" />
  </svg>
);

const TrailIcon = ({ size = 14 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5" cy="19" r="2.2" />
    <circle cx="19" cy="5" r="2.2" />
    <path d="M6.6 17.4C10 14 8 11 12 9.5c3.2-1.2 4-2 5.4-2.9" strokeDasharray="3 2.4" />
  </svg>
);

const FitIcon = ({ size = 14 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 4H5a1 1 0 0 0-1 1v4M15 4h4a1 1 0 0 1 1 1v4M9 20H5a1 1 0 0 1-1-1v-4M15 20h4a1 1 0 0 0 1-1v-4" />
  </svg>
);

export default function WikiGraphPanel(props: Props) {
  const { notes, sequence, currentId, mode, fullNodes, fullEdges, fullLoading, conversationId } = props;
  const { t } = useLanguage();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // WebGL layer under the 2D canvas — the full map's edges/nodes render on
  // the GPU for Obsidian-smooth motion; labels/focus stay on the 2D canvas.
  const glCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // One engine per mode: each keeps its layout and camera, so toggling back
  // and forth is instant and doesn't re-run the simulation.
  const trailEngine = useRef<WikiForceGraph>();
  const fullEngine = useRef<WikiForceGraph>();
  if (!trailEngine.current) trailEngine.current = new WikiForceGraph();
  if (!fullEngine.current) fullEngine.current = new WikiForceGraph();

  // Camera intent: 'fit' keeps everything framed, 'focus' follows the note the
  // agent is reading (full mode), 'free' = the user took over (pan/zoom/drag).
  const followRef = useRef<'fit' | 'focus' | 'free'>('fit');
  const hoverRef = useRef<string | null>(null);
  const dragRef = useRef<
    | { kind: 'pan'; x: number; y: number; moved: number }
    | { kind: 'node'; id: string; moved: number }
    | null
  >(null);

  // --- Source preview: click a note to read its markdown in place ----------
  const [preview, setPreview] = useState<{
    noteId: string;
    title: string;
    wikiName: string;
    content: string;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const previewReqId = useRef(0);

  const openPreview = (noteId: string, title: string, pageRef: string) => {
    if (!conversationId) return;
    const reqId = ++previewReqId.current;
    setPreview({ noteId, title, wikiName: '', content: '', loading: true, error: null });
    api.get(`/wiki/conversation/${conversationId}/page`, { params: { page: pageRef } })
      .then((res) => {
        if (previewReqId.current !== reqId) return;  // a newer click won
        setPreview({
          noteId,
          title: res.data?.title || title,
          wikiName: res.data?.wiki_name || '',
          content: res.data?.content || '',
          loading: false,
          error: null,
        });
      })
      .catch(() => {
        if (previewReqId.current !== reqId) return;
        setPreview({ noteId, title, wikiName: '', content: '', loading: false, error: t('wiki.preview.error') });
      });
  };
  const closePreview = () => { previewReqId.current++; setPreview(null); };

  // --- Feed the engines -----------------------------------------------------
  // Trail edges: the agent's temporal jump path (dashed, arrowed) PLUS the
  // real wikilinks between visited notes (solid, from the prefetched graph).
  // Without the real links the trail is a pure reading chain and always lays
  // out as a line; the links make it the actual subgraph the agent explored.
  const trailEdges = useMemo(() => {
    const out: GraphEdgeInput[] = [];
    const pairs = new Set<string>();
    const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
    for (let i = 1; i < sequence.length; i++) {
      const a = sequence[i - 1]; const b = sequence[i];
      if (a === b || pairs.has(pairKey(a, b))) continue;
      pairs.add(pairKey(a, b));
      out.push({ source: a, target: b, kind: 'jump' });
    }
    if (fullEdges.length && notes.length > 1) {
      const visited = new Set(notes.map((n) => n.id));
      for (const e of fullEdges) {
        const a = baseKey(e.source); const b = baseKey(e.target);
        if (a === b || !visited.has(a) || !visited.has(b) || pairs.has(pairKey(a, b))) continue;
        pairs.add(pairKey(a, b));
        out.push({ source: a, target: b, kind: 'link' });
      }
    }
    return out;
  }, [sequence, notes, fullEdges]);

  useEffect(() => { trailEngine.current!.setData(notes, trailEdges, TRAIL_STYLE); }, [notes, trailEdges]);
  useEffect(() => { fullEngine.current!.setData(fullNodes, fullEdges, FULL_STYLE); }, [fullNodes, fullEdges]);

  // Full mode draws visited notes highlighted; the trail records basenames
  // while the full graph uses repo paths, so match on basename.
  const visitedFullIds = useMemo(() => {
    if (!fullNodes.length) return new Set<string>();
    const visited = new Set(notes.map((n) => n.id));
    return new Set(fullNodes.filter((n) => visited.has(baseKey(n.id))).map((n) => n.id));
  }, [notes, fullNodes]);

  const currentFullId = useMemo(() => {
    if (!currentId) return null;
    return fullNodes.find((n) => baseKey(n.id) === currentId)?.id ?? null;
  }, [currentId, fullNodes]);

  // Stats chip counts — derived from props (the engine's fields are mutable
  // refs and wouldn't re-render). Links counted undirected, like the drawing.
  const fullLinkCount = useMemo(() => {
    const seen = new Set<string>();
    for (const e of fullEdges) {
      if (e.source === e.target) continue;
      seen.add(e.source < e.target ? `${e.source}|${e.target}` : `${e.target}|${e.source}`);
    }
    return seen.size;
  }, [fullEdges]);

  // Mode switch re-arms the camera for that mode's default framing.
  useEffect(() => { followRef.current = mode === 'full' ? 'focus' : 'fit'; }, [mode]);

  // The current-note ring pulses while the agent is actively moving (20s after
  // each jump), then freezes so idle frames can skip entirely.
  const lastCurrentChange = useRef(0);
  useEffect(() => { lastCurrentChange.current = performance.now(); }, [currentId]);

  // --- Animation loop: tick the simulation under a time budget, steer the
  // camera, draw. The sim sleeps once settled, so idle frames are cheap.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const cv = canvasRef.current;
      if (cv) {
        const engine = (mode === 'full' ? fullEngine : trailEngine).current!;
        engine.tick();
        const vw = cv.clientWidth; const vh = cv.clientHeight;
        if (vw > 0 && engine.nodeCount > 0) {
          const follow = followRef.current;
          if (follow === 'fit') {
            engine.fitAll(vw, vh, mode === 'full' ? 1.5 : 1.4);
          } else if (follow === 'focus') {
            const focusId = mode === 'full' ? currentFullId : currentId;
            if (focusId && engine.getNode(focusId)) {
              const zoom = Math.min(1.4, Math.max(0.85, engine.fitScale(vw, vh, 1.5) * 2.2));
              engine.centerOn(focusId, vw, vh, zoom);
            } else {
              engine.fitAll(vw, vh, 1.5);
            }
          }
        }
        engine.draw(cv, {
          mode,
          currentId: mode === 'full' ? currentFullId : currentId,
          hoverId: hoverRef.current,
          visited: mode === 'full' ? visitedFullIds : null,
          time: performance.now(),
          accent: ACCENT,
          folderColor,
          alwaysLabel: mode === 'trail',
          nodeColor: mode === 'full' ? MAP_NODE_COLOR : undefined,
          highlight: MAP_HIGHLIGHT,
          pulse: !!currentId && performance.now() - lastCurrentChange.current < 20_000,
        }, mode === 'full' ? glCanvasRef.current : null);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [mode, currentId, currentFullId, visitedFullIds]);

  // --- Pointer interaction: pan, zoom-to-cursor, hover, drag-a-node, click --
  const engineNow = () => (mode === 'full' ? fullEngine : trailEngine).current!;

  const onWheel = (e: React.WheelEvent) => {
    const cv = canvasRef.current; if (!cv) return;
    const rect = cv.getBoundingClientRect();
    engineNow().zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.15 : 0.87);
    followRef.current = 'free';
  };

  const onDown = (e: React.MouseEvent) => {
    const cv = canvasRef.current; if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const hit = engineNow().hitTest(e.clientX - rect.left, e.clientY - rect.top);
    dragRef.current = hit
      ? { kind: 'node', id: hit.id, moved: 0 }
      : { kind: 'pan', x: e.clientX, y: e.clientY, moved: 0 };
  };

  const onMove = (e: React.MouseEvent) => {
    const cv = canvasRef.current; if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const sx = e.clientX - rect.left; const sy = e.clientY - rect.top;
    const engine = engineNow();
    const drag = dragRef.current;
    if (!drag) {
      const hit = engine.hitTest(sx, sy);
      hoverRef.current = hit?.id ?? null;
      cv.style.cursor = hit ? 'pointer' : 'grab';
      return;
    }
    drag.moved += Math.abs(e.movementX) + Math.abs(e.movementY);
    if (drag.kind === 'pan') {
      engine.panBy(e.clientX - drag.x, e.clientY - drag.y);
      drag.x = e.clientX; drag.y = e.clientY;
      followRef.current = 'free';
      cv.style.cursor = 'grabbing';
    } else {
      const node = engine.getNode(drag.id);
      if (node) {
        const w = engine.screenToWorld(sx, sy);
        node.fx = w.x; node.fy = w.y;
        engine.reheat(0.25);
        followRef.current = 'free';
        cv.style.cursor = 'grabbing';
      }
    }
  };

  const onUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    const cv = canvasRef.current;
    if (cv) cv.style.cursor = 'grab';
    if (!drag || drag.kind !== 'node') return;
    const node = engineNow().getNode(drag.id);
    if (node) { node.fx = null; node.fy = null; }
    if (drag.moved < 5 && node) {
      // A click, not a drag → open the note's source. The trail stores bare
      // titles; the full graph stores repo paths — both resolve server-side.
      openPreview(node.id, node.label, mode === 'full' ? node.id : node.label);
    }
  };

  const onLeave = () => { hoverRef.current = null; onUp(); };

  const fitView = () => {
    followRef.current = 'fit';
  };

  const fullReady = !fullLoading && fullNodes.length > 0;

  return (
    // Body-only: the unified side-panel dock (ConversationPanel) owns all
    // chrome — tabs, collapse, close — and the panel's width.
    <aside className="wiki-panel">
      <div
        className="wiki-panel__resize"
        onMouseDown={(e) => {
          const startX = e.clientX;
          // The unified dock owns the width — measure it as the drag baseline
          // (RTL-aware). Clamping lives in the dock's shared setter.
          const dockEl = (e.currentTarget as HTMLElement).closest('.side-panel-dock') as HTMLElement | null;
          const startW = dockEl ? dockEl.getBoundingClientRect().width : 460;
          const rtl = getComputedStyle(document.documentElement).direction === 'rtl';
          // Same drag-time transition disable as the artifact handle, so the
          // panel tracks the pointer 1:1 instead of easing 180ms behind it.
          document.body.classList.add('artifact-resizing');
          const move = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            props.onResize(rtl ? startW + dx : startW - dx);
          };
          const up = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            document.body.classList.remove('artifact-resizing');
          };
          window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
        }}
      >
        <span className="wiki-panel__resize-grip" />
      </div>
      <div className="wiki-panel__body">
        <canvas
          ref={glCanvasRef}
          className="wiki-panel__canvas-gl"
          style={{ display: mode === 'full' ? 'block' : 'none' }}
          aria-hidden
        />
        <canvas
          ref={canvasRef}
          className="wiki-panel__canvas"
          onWheel={onWheel}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onLeave}
        />

        {/* Floating view controls — segmented Trail / Full map + re-frame. */}
        {!preview && (
          <div className="wiki-panel__controls">
            <div className="wiki-panel__seg" role="group" aria-label={t('wiki.graph.title')}>
              <button
                type="button"
                className={`wiki-panel__seg-btn ${mode === 'trail' ? 'is-active' : ''}`}
                onClick={() => props.onSetMode('trail')}
                aria-pressed={mode === 'trail'}
              >
                <TrailIcon />
                {t('wiki.graph.modeTrail')}
              </button>
              <button
                type="button"
                className={`wiki-panel__seg-btn ${mode === 'full' ? 'is-active' : ''}`}
                onClick={() => props.onSetMode('full')}
                aria-pressed={mode === 'full'}
              >
                <NetworkIcon size={14} />
                {t('wiki.graph.modeFull')}
              </button>
            </div>
            <button type="button" className="wiki-panel__fit-btn" onClick={fitView} title={t('wiki.graph.fit')}>
              <FitIcon />
            </button>
          </div>
        )}

        {mode === 'full' && fullReady && !preview && (
          <div className="wiki-panel__stats">
            {t('wiki.graph.stats', { notes: fullNodes.length, links: fullLinkCount })}
          </div>
        )}

        {mode === 'trail' && notes.length === 0 && !preview && (
          <div className="wiki-panel__empty">{t('wiki.graph.empty')}</div>
        )}
        {mode === 'full' && fullLoading && !preview && (
          <div className="wiki-panel__loading">
            <span className="wiki-panel__spinner" aria-hidden />
            {t('wiki.graph.loading')}
          </div>
        )}
        {mode === 'full' && !fullLoading && fullNodes.length === 0 && !preview && (
          <div className="wiki-panel__empty">{t('wiki.graph.fullEmpty')}</div>
        )}

        {/* Source preview — the visited note's markdown, rendered on a document
            sheet like the artifact panel. Overlays the graph; a back button (or
            clicking another source) returns to it. */}
        {preview && (
          <div className="wiki-panel__preview">
            <div className="wiki-panel__preview-bar">
              <button
                type="button"
                className="wiki-panel__preview-back"
                onClick={closePreview}
                title={t('wiki.preview.backToGraph')}
              >
                <svg className="rtl-flip" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                {t('wiki.preview.backToGraph')}
              </button>
              <div className="wiki-panel__preview-titles">
                <span className="wiki-panel__preview-title" title={preview.title} dir="auto">{preview.title}</span>
                {preview.wikiName && <span className="wiki-panel__preview-wiki">{preview.wikiName}</span>}
              </div>
            </div>
            <div className="wiki-panel__preview-body">
              {preview.loading ? (
                <div className="wiki-panel__preview-state">{t('wiki.preview.loading')}</div>
              ) : preview.error ? (
                <div className="wiki-panel__preview-state">{preview.error}</div>
              ) : (
                <div className="wiki-doc">
                  <div className="wiki-doc__page wiki-prose">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripWikiLinks(preview.content)}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <footer className="wiki-panel__footer">
        <div dir="auto" className="wiki-panel__footer-label">
          {preview
            ? t('wiki.preview.reading', { page: preview.title })
            : currentId
              ? t('wiki.graph.reading', { page: (notes.find((n) => n.id === currentId)?.label) || currentId })
              : t('wiki.graph.idle')}
        </div>
        {notes.length > 0 && (
          <ol className="wiki-panel__crumbs" data-count={notes.length}>
            {notes.map((n) => {
              const active = preview ? preview.noteId === n.id : n.id === currentId;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`wiki-panel__crumb-btn ${active ? 'is-current' : ''}`}
                    title={t('wiki.preview.openSource', { page: n.label })}
                    onClick={() => openPreview(n.id, n.label, n.label)}
                    disabled={!conversationId}
                  >
                    {n.label}
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </footer>
    </aside>
  );
}
